import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiBase } from '../utils/apiConfig';
import { PROVINCES, citiesFor } from '../features/onboarding/geography.js';
import { SERVICE_OPTIONS } from '../features/onboarding/onboardingMapping.js';
import MovdInput from '../design-system/MovdInput.jsx';
import MovdButton from '../design-system/MovdButton.jsx';
import MovdNotice from '../design-system/MovdNotice.jsx';
import { movdTokens } from '../design-system/movdTokens.js';

const steps = [
  { num: '01', title: 'Create your account', body: 'Tell us about your company, home base, and the services you provide.' },
  { num: '02', title: 'Finish your setup', body: 'Add your service area, availability, and pricing in the guided partner setup.' },
  { num: '03', title: 'Prepare to go live', body: 'Complete the required business details so your company can be reviewed for MOVD.' },
];

const cardStyle = { padding: 28, height: '100%', borderRadius: 8, border: '1px solid #dce9e7', background: '#fff' };

export default function BecomePartner() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null);
  const [formData, setFormData] = useState({
    email: '', password: '', companyName: '', phone: '', yearsInBusiness: '',
    homeProvince: '', homeCity: '',
    services: { residential: true, commercial: false, longDistance: false, packing: false, specialty: false, assembly: false },
  });

  const set = field => e => setFormData(p => ({ ...p, [field]: e.target.value }));
  const toggleService = id => setFormData(p => ({ ...p, services: { ...p.services, [id]: !p.services[id] } }));

  const scrollToForm = () => document.getElementById('partner-form')?.scrollIntoView({ behavior: 'smooth' });

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${getApiBase()}/movers/register`, {
        name: formData.companyName,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        yearsInBusiness: formData.yearsInBusiness || undefined,
        provinces: formData.homeProvince ? [formData.homeProvince] : [],
        locations: formData.homeCity ? [formData.homeCity] : [],
        longDistanceMoves: formData.services.longDistance,
        residentialMoves: formData.services.residential,
        commercialMoves: formData.services.commercial,
        services: ['moving', ...(formData.services.packing ? ['packing'] : []), ...(formData.services.specialty ? ['specialty'] : []), ...(formData.services.assembly ? ['assembly'] : [])],
      }, { withCredentials: true });

      // Keep the returned token so the partner portal skips the login screen and
      // drops the new mover straight into the guided onboarding wizard.
      const token = res?.data?.token;
      if (token) {
        localStorage.setItem('token', token);
        localStorage.setItem('userData', JSON.stringify({ email: formData.email, name: formData.companyName, role: 'mover' }));
      }

      setNotice({ severity: 'success', text: 'Account created! Opening your setup wizard…' });
      setTimeout(() => navigate('/mover-portal'), 1200);
    } catch (err) {
      setNotice({ severity: 'error', text: err?.response?.data?.error || 'Registration failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: movdTokens.color.background.canvas, minHeight: '100vh', overflowX: 'hidden' }}>
      <div style={{ background: '#e5f7f4', padding: 'clamp(52px, 8vw, 88px) 20px', borderBottom: '1px solid #d0e9e4' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto', display: 'grid', gridTemplateColumns: 'minmax(0, 1.15fr) minmax(260px, .85fr)', gap: 48, alignItems: 'end' }}>
          <div>
            <p style={{ margin: '0 0 16px', color: '#008d7b', fontWeight: 900, letterSpacing: '.1em', fontSize: 12 }}>FOR MOVING COMPANIES</p>
            <h1 style={{ fontWeight: 900, margin: '0 0 20px', color: '#102449', fontSize: 'clamp(2.5rem, 5vw, 4.25rem)', lineHeight: .98, letterSpacing: 0 }}>Build your mover profile.</h1>
            <p style={{ margin: 0, color: '#405875', fontSize: '1.08rem', lineHeight: 1.6, maxWidth: 590 }}>Create your MOVD partner account, set up your business details, and prepare your company for customer bookings.</p>
          </div>
          <div style={{ padding: 28, background: '#fff', border: '1px solid #cfe7e2', borderRadius: 8 }}>
            <p style={{ margin: '0 0 6px', color: '#102449', fontSize: '1rem', fontWeight: 900 }}>Already a MOVD partner?</p>
            <p style={{ margin: '0 0 18px', color: '#52637c', fontSize: '.9rem', lineHeight: 1.5 }}>Sign in to manage your bookings, availability, and company profile.</p>
            <button type="button" onClick={() => navigate('/mover-portal')} style={{ background: 'transparent', border: 'none', color: '#008d7b', fontWeight: 800, cursor: 'pointer', padding: 0, fontSize: '.9rem' }}>Sign in to your portal →</button>
          </div>
        </div>
        <div style={{ maxWidth: 1120, margin: '32px auto 0' }}>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
            <MovdButton onClick={scrollToForm}>Create your account</MovdButton>
          </div>
        </div>
      </div>

      <div style={{ padding: 'clamp(48px, 7vw, 72px) 20px', background: movdTokens.color.background.canvas }}>
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
          <div style={{ marginBottom: 32 }}>
            <p style={{ margin: '0 0 10px', color: '#008d7b', fontWeight: 900, letterSpacing: '.1em', fontSize: 12 }}>A CLEAR START</p>
            <h2 style={{ margin: 0, fontWeight: 900, color: '#102449', fontSize: '2.25rem', letterSpacing: 0 }}>What happens next</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 }}>
            {steps.map(s => (
              <div key={s.num} style={{ ...cardStyle, position: 'relative' }}>
                <span style={{ position: 'absolute', top: 20, right: 24, fontSize: 30, fontWeight: 900, color: '#c7e5e0', lineHeight: 1 }}>{s.num}</span>
                <h3 style={{ margin: '0 0 12px', fontWeight: 800, color: movdTokens.color.content.primary, fontSize: 20, paddingRight: 48 }}>{s.title}</h3>
                <p style={{ margin: 0, color: movdTokens.color.content.secondary, lineHeight: 1.7 }}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div id="partner-form" style={{ padding: 'clamp(48px, 7vw, 72px) 20px', background: '#edf8f6', borderTop: '1px solid #d9eeea' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <p style={{ margin: '0 0 12px', color: '#008d7b', fontWeight: 900, letterSpacing: '.1em', fontSize: 12 }}>CREATE YOUR ACCOUNT</p>
            <h2 style={{ margin: '0 0 12px', fontWeight: 900, color: '#102449', fontSize: '2.4rem', letterSpacing: 0 }}>Start your company setup</h2>
            <p style={{ margin: 0, color: '#64748B', maxWidth: 520, marginLeft: 'auto', marginRight: 'auto' }}>Free to join. Once your account is created, a short guided setup walks you through pricing, service area, and availability.</p>
          </div>

          <div style={{ padding: 'clamp(20px, 4vw, 40px)', borderRadius: 8, border: '1px solid #cfe7e2', background: '#fff' }}>
            <form onSubmit={handleSubmit} noValidate style={{ display: 'grid', gap: 32 }}>
              <div>
                <p style={{ margin: '0 0 12px', color: '#008d7b', fontWeight: 800, letterSpacing: '.1em', fontSize: 12, textTransform: 'uppercase' }}>Account</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                  <MovdInput label="Email address" type="email" value={formData.email} onChange={set('email')} required />
                  <MovdInput label="Password" type="password" value={formData.password} onChange={set('password')} required />
                </div>
              </div>

              <div>
                <p style={{ margin: '0 0 12px', color: '#008d7b', fontWeight: 800, letterSpacing: '.1em', fontSize: 12, textTransform: 'uppercase' }}>Company info</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 16 }}>
                  <MovdInput label="Company name" value={formData.companyName} onChange={set('companyName')} required />
                  <MovdInput label="Phone number" value={formData.phone} onChange={set('phone')} required />
                  <MovdInput label="Years in business" type="number" value={formData.yearsInBusiness} onChange={set('yearsInBusiness')} />
                  <label style={{ display: 'grid', gap: 8, color: movdTokens.color.tealSoft, fontSize: 11, fontWeight: 900, letterSpacing: '.14em', textTransform: 'uppercase' }}>
                    <span>Home province</span>
                    <select value={formData.homeProvince} onChange={e => setFormData(p => ({ ...p, homeProvince: e.target.value, homeCity: '' }))} required style={{ minHeight: 52, borderRadius: movdTokens.radius.control, border: '1px solid rgba(0,0,0,.15)', padding: '0 12px', font: 'inherit', fontSize: 15, fontWeight: 600, textTransform: 'none', color: movdTokens.color.ink }}>
                      <option value="" disabled>Select a province</option>
                      {PROVINCES.map(p => <option key={p.code} value={p.code}>{p.name}</option>)}
                    </select>
                  </label>
                  <label style={{ display: 'grid', gap: 8, color: movdTokens.color.tealSoft, fontSize: 11, fontWeight: 900, letterSpacing: '.14em', textTransform: 'uppercase' }}>
                    <span>Home city</span>
                    <select value={formData.homeCity} onChange={set('homeCity')} required disabled={!formData.homeProvince} style={{ minHeight: 52, borderRadius: movdTokens.radius.control, border: '1px solid rgba(0,0,0,.15)', padding: '0 12px', font: 'inherit', fontSize: 15, fontWeight: 600, textTransform: 'none', color: movdTokens.color.ink }}>
                      <option value="" disabled>Select a city</option>
                      {citiesFor(formData.homeProvince).map(city => <option key={city} value={city}>{city}</option>)}
                    </select>
                  </label>
                </div>
                <p style={{ margin: '0 0 8px', color: '#475569', fontWeight: 700, fontSize: 14 }}>Services you offer</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
                  {SERVICE_OPTIONS.map(option => (
                    <label key={option.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, width: 'calc(33% - 11px)', minWidth: 140 }}>
                      <input type="checkbox" checked={formData.services[option.id]} onChange={() => toggleService(option.id)} />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>

              <MovdNotice tone="info">You&apos;ll set your rates, full service area, and availability next in the guided setup — it takes about 5 minutes.</MovdNotice>

              <MovdButton type="submit" loading={loading} style={{ width: '100%' }}>Create account & start setup</MovdButton>

              <p style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center', color: movdTokens.color.content.secondary, fontSize: 12, margin: 0 }}><span aria-hidden="true">🛡</span> Your information is secure and never shared</p>

              <p style={{ textAlign: 'center', color: movdTokens.color.content.secondary, fontSize: 14, margin: 0 }}>
                Already have an account?{' '}
                <button type="button" onClick={() => navigate('/mover-portal')} style={{ background: 'transparent', border: 'none', color: '#008d7b', fontWeight: 700, cursor: 'pointer', padding: 0 }}>Sign in here</button>
              </p>
            </form>
          </div>

        </div>
      </div>

      {notice && <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1500, minWidth: 280 }}><MovdNotice tone={notice.severity}>{notice.text}</MovdNotice></div>}
    </div>
  );
}
